<script setup lang="ts">
import ConnectionsPage from '@/components/CalendarSetup/ConnectionsPage.vue'
import ExtrasPage from '@/components/CalendarSetup/ExtrasPage.vue'
import FormsPage from '@/components/CalendarSetup/FormsPage.vue'
import MeetingDetailsPage from '@/components/CalendarSetup/MeetingDetailsPage.vue'
import NewAvailabilityPage from '@/components/CalendarSetup/NewAvailabilityPage.vue'
import NewAvailabilityPageWithSchedule from '@/components/CalendarSetup/NewAvailabilityPageV2.vue'
import NotificationsPageV2 from '@/components/CalendarSetup/NotificationsPageV2.vue'
import { useI18n } from 'vue-i18n'

import EventCalendarAlert from '@/components/CalendarSetup/EventCalendarAlert.vue'
import NavLink from '@/components/CalendarSetup/NavLink.vue'

import CalendarShareModal from '@/components/Settings/CalendarShareModal.vue'
import calendarScheduleState from '@/states/calendarScheduleState'
import { ICalendarShare, SHARE_SOURCE } from '@/types'
import {
  ArrowNarrowLeftIcon,
  Copy04Icon,
  Lightbulb02Icon,
  Share06Icon,
  Tool02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UIButton, UITooltip, useNotification } from '@gohighlevel/ghl-ui'
import _, { isEqual } from 'lodash'
import { computed, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { CalendarResourceService } from '../class/services/CalendarResourceService'
import { CalendarService } from '../class/services/CalendarService'
import RoomsAndEquipmentsPage from '../components/CalendarSetup/RoomsAndEquipmentsPage.vue'
import Calendar, { CalendarType, DEFAULT_CALENDAR_VALUES } from '../models/calendar'
import { CALENDAR_RESOURCE_TYPES } from '../models/calendar_resource'
import AppState, {
  getCalendarsInLocation,
  getCustomValues,
  getEditingCalendar,
  getEquipmentsInLocation,
  getMerchantInLocation,
  getRoomsInLocation,
  isEquipmentEnabled,
  isRoomEnabled,
  setIsAdvancedCalendarPageLoading,
  updateCalendarStore,
} from '../states/app'
import { toggleAdvancedView } from '../utils/commonModalFunctions'
import { copyToClipboard, isPaymentAllowed } from '../utils/generalFunctions'
import { getQuickTip } from '../utils/quickTips'

const toast = useToast()

const meetingDetailsPage = ref()
const availabilityPage = ref()
const formPage = ref()
const notificationsPage = ref()
const extrasPage = ref()
const connectionsPage = ref()
const roomsPage = ref()
const showCalendarResourcesTab = ref(false)
const route = useRoute()
const showShareCalendarModal = ref(false)

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  isSaving: false,
  currentStep: 0,
  currentQuickTip: '',
  formData: {
    name: '',
    officeHours: [],
    eventColor: '#D50000',
    teamMembers: [],
    group: '',
    appoinmentPerSlot: '1',
    description: '',
    slotDuration: '30',
    slug: '',
    fileList: [],
    slotDurationUnit: 'mins',
    stripe: {
      amount: '0',
      currency: 'USD',
      chargeDescription: '',
    },
  },
  showConnectionSyncModal: false,
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar as Calendar
})

const methods = {
  async loadData() {
    setIsAdvancedCalendarPageLoading(true)
    
    // Check if we're creating from draft data (no calendar ID yet)
    if (AppState.isNewCalendar && !AppState.selectedCalendarId && AppState.draftCalendarData) {
      // Load necessary data but don't load an existing calendar
      await Promise.all([
        getMerchantInLocation(),
        getCustomValues(),
        isPaymentAllowed(AppState.locationId),
      ])
      
      // Create a complete calendar object from draft data with all required defaults
      AppState.editingCalendar = new Calendar({
        ...AppState.draftCalendarData,
        // Add missing required properties with defaults
        id: null, // No ID yet, will be assigned on save
        widgetLogo: { url: '', shape: 'square' },
        widgetSlugUrl: '',
        isActive: true,
        isLivePaymentMode: AppState.draftCalendarData.shouldAcceptPayment || false,
        enableRecurring: false,
        availabilityType: 0,
        enableConsentCheck: true, // Enable consent checkbox by default
        consentLabel: DEFAULT_CALENDAR_VALUES.CONSENT_LABEL,
        eventColor: '#D50000',
        eventTitle: AppState.draftCalendarData.eventTitle || '{{contact.name}}', // Default event title
        groupId: '',
        formSubmitType: 'ThankYouMessage',
        formSubmitRedirectURL: '',
        formSubmitThanksMessage: DEFAULT_CALENDAR_VALUES.THANK_YOU_MESSAGE,
        autoConfirm: true, // Enable auto-confirm by default
        shouldSendAlertEmailsToAssignedMember: false,
        shouldSkipAssigningContactOwner: false,
        appoinmentTitle: '',
        notes: DEFAULT_CALENDAR_VALUES.MEETING_NOTES,
        slotInterval: 15,
        slotIntervalUnit: 'mins',
        preBufferUnit: 'mins',
        slotBufferUnit: 'mins',
        preBuffer: 0,
        slotBuffer: 0,
        enableDates: [],
        deletedDates: [],
        stickyContact: true,
        customFieldsForContactOnWidget: [],
        guestType: '',
        pixelId: '',
        allowReschedule: true,
        allowCancellation: true,
        shouldAssignContactToTeamMember: true,
        shouldSkipContactCreate: false,
        notifications: [],
        availabilityTimeConstraintType: 'range_dates',
        availabilityStartDate: null,
        availabilityEndDate: null,
        linkedCalendars: {},
        stripe: AppState.draftCalendarData.stripe || {},
        googleInvitationEmails: false,
        locationConfigurations: AppState.draftCalendarData.calendarType === CalendarType.EVENT 
          ? [{ kind: 'custom', location: AppState.draftCalendarData.meetingLocation || '', position: 0 }]
          : [],
      })
    } else {
      // Normal flow: loading existing calendar
      const calendarId = AppState.selectedCalendarId as string
      await Promise.all([
        getMerchantInLocation(),
        getCustomValues(),
        getEditingCalendar(calendarId),
        isPaymentAllowed(AppState.locationId),
      ])
    }
    
    setIsAdvancedCalendarPageLoading(false)
  },
  async handleCancel() {
    // Clear draft data (no need to delete from database - it was never created)
    AppState.draftCalendarData = null
    AppState.editingCalendar = null
    toggleAdvancedView()
    AppState.selectedCalendarId = ''
    AppState.isNewCalendar = false
    await getCalendarsInLocation(true)
    calendarScheduleState.clearState()
  },
  async isCalendarDetailsChanged(payload) {
    //Keys on which we need to check if they are changed and need to update the calendars store
    const keysToCheck = [
      'name',
      'slotDuration',
      'slotDurationUnit',
      'enableRecurring',
      'teamMembers',
      'groupId',
    ]

    return keysToCheck.some(key => {
      if (key === 'teamMembers') {
        return !isEqual(editingCalendar.value.teamMembers, payload.teamMembers)
      }
      return editingCalendar.value[key] !== payload[key]
    })
  },

  async handleSave() {
    state.isSaving = true
    AppState.isConnectionSyncLoading = true
    const meetingDetailsPageData =
      await meetingDetailsPage.value.methods.emitSave()

    const availabilityPageData = await availabilityPage.value.methods.emitSave()

    const formPageData = await formPage.value.methods.emitSave()

    const notificationsPageData =
      await notificationsPage.value.methods.emitSave()

    const connectionsPageData = await connectionsPage.value.methods.emitSave()

    const extrasPageData = await extrasPage.value.methods.emitSave()

    const roomsPageData = showCalendarResourcesTab.value
      ? await roomsPage.value.methods.emitSave()
      : null

    if (!meetingDetailsPageData) {
      state.currentStep = 0
      state.isSaving = false
      return
    } else if (!availabilityPageData) {
      state.currentStep = 1
      state.isSaving = false
      return
    } else if (!formPageData) {
      state.currentStep = 2
      state.isSaving = false
      return
    } else if (!notificationsPageData) {
      state.currentStep = 3
      state.isSaving = false
      return
    } else if (!connectionsPageData) {
      state.currentStep = 4
      state.isSaving = false
      return
    } else if (!extrasPageData) {
      state.currentStep = 5
      state.isSaving = false
      return
    } else if (!roomsPageData && showCalendarResourcesTab.value) {
      state.currentStep = 6
      state.isSaving = false
    }

    const payload = {
      ...meetingDetailsPageData,
      ...availabilityPageData,
      ...formPageData,
      ...notificationsPageData,
      ...connectionsPageData,
      ...extrasPageData,
      calendarType: editingCalendar.value.calendarType, // Ensure calendarType is always included
    }
    const updateCalendars = await methods.isCalendarDetailsChanged(payload)

    if (formPageData.widgetConfig?.pageOrder) {
      payload.widgetConfig.pageOrder = formPageData.widgetConfig?.pageOrder
    }
    if (roomsPageData && showCalendarResourcesTab.value) {
      await methods.updateRoomsAndEquipments(roomsPageData)
    }

    if (editingCalendar.value.calendarType !== CalendarType.EVENT) {
      if (payload.linkedCalendars?.google?.id) {
        CalendarService.unlinkGoogleCalendar(editingCalendar.value.id as string)
      }
      payload.linkedCalendars = {}
    }

    if (editingCalendar.value?.isServiceCalendar) {
      payload.appoinmentPerSlot = meetingDetailsPageData.teamMembers?.length
    }
    if (
      connectionsPageData?.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id !==
        connectionsPageData?.linkedCalendars?.google?.id
    ) {
      await CalendarService.unlinkGoogleCalendar(
        editingCalendar.value.id as string
      )
    }

    await methods.updateCalendar(payload, state.currentStep, updateCalendars)
    if (
      !connectionsPageData?.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id
    ) {
      await CalendarService.unlinkGoogleCalendar(
        editingCalendar.value.id as string
      )
    } else if (
      connectionsPageData?.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id !==
        connectionsPageData?.linkedCalendars?.google?.id
    ) {
      CalendarService.linkGoogleCalendar(editingCalendar.value.id as string)
    }
  },
  async updateCalendar(data, currentStep, updateCalendars = false) {
    try {
      // remove 'availabilityType' as its no longer needed delete deletex
      delete data.availabilityType

      // Check if this is a new calendar from draft data
      if (AppState.isNewCalendar && !AppState.selectedCalendarId && AppState.draftCalendarData) {
        // CREATE the calendar for the first time
        // Ensure locationId is included (required for creation)
        const createPayload = {
          ...data,
          locationId: AppState.locationId,
        }
        
        const { data: createdCalendar } = await CalendarService.create(createPayload)
        
        // Update state with the newly created calendar ID
        AppState.selectedCalendarId = createdCalendar.calendar.id
        AppState.editingCalendar = createdCalendar.calendar
        
        await methods.saveRespectiveSchedule(data)
        
        // Clear draft data and mark as no longer new
        AppState.draftCalendarData = null
        AppState.isNewCalendar = false
      } else {
        // UPDATE existing calendar
        await CalendarService.update(editingCalendar.value.id as string, data)
        await methods.saveRespectiveSchedule(data)
        
        // Mark calendar as saved (no longer a draft)
        if (AppState.isNewCalendar) {
          AppState.isNewCalendar = false
        }
      }

      if (data?.reserveService && AppState.merchant?.merchant_id) {
        const servicePayload = {
          calender_id: editingCalendar.value.id,
          merchant_id: AppState.merchant?.merchant_id,
          home_service_data: {
            category_type: data.homeServiceData?.categoryType,
            job_type: data.homeServiceData?.jobType,
          },
        } as any
        if (data?.price && data.price.priceMicros >= 0) {
          servicePayload.price = {
            currency_code: data.price?.currencyCode || 'USD',
            price_micros: data.price?.priceMicros || 0,
          }
        }
        await CalendarService.createService(servicePayload)
      } else {
        await CalendarService.deleteReserveService(editingCalendar.value.id)
      }
      
      notification.success({
        content: t('calendar_advanced.general.saveCalendarSuccess'),
        duration: 2 * 1000,
      })
      
      if (state.currentStep === currentStep) {
        debouncedCalendarRefresh()
      }

      if (updateCalendars) {
        updateCalendarStore()
      }

      // if (state.currentStep === 5) {
      //   toggleAdvancedView()

      //   router.push({
      //     name: 'calendar-settings__default',
      //     params: {
      //       locationId: AppState.locationId,
      //     },
      //   })
      // }

      // state.isSaving = false
      // state.currentStep += 1
    } catch (error: any) {
      const message = error?.response?.data?.message
      toast.error(
        Array.isArray(message) ? message[0] : message || 'Something went wrong'
      )
      methods.updateShowCalendarSyncModal(false)
      AppState.isConnectionSyncLoading = false
      state.isSaving = false
    }
  },
  async saveRespectiveSchedule(calendar: any) {
    try {
      if (
        AppState.isScheduleEnabled &&
        editingCalendar.value?.calendarType === CalendarType.EVENT
      ) {
        const schedule = await calendarScheduleState.getEventCalendarSchedules(
          editingCalendar.value?.id
        )
        schedule.openHoursAndAvailabilitiesToRules(
          calendar?.openHours,
          calendar?.availabilities
        )
        if (!schedule.id) {
          // Create schedule from the open hours for new calendars
          const { rules, name } = schedule.toJSON()
          await calendarScheduleState.createSchedule({
            rules,
            timezone: schedule.timezone || AppState.locationTimezone,
            calendarIds: [editingCalendar.value?.id],
            isCustom: true,
            locationId: AppState.locationId,
            name: name || editingCalendar.value?.name,
            userId: undefined, // EVENT calendars don't have a specific user
          })
          return
        }
        // Update existing schedule
        await CalendarService.updateCalendarSchedule(schedule.id, {
          rules: schedule.rules,
          timezone: schedule.timezone,
          calendarIds: schedule.calendarIds,
          isCustom: true,
        })
      }
    } catch (error) {
      console.error('Error saving respective schedule', error)
    }
  },
  async refreshCalendar() {
    await getEditingCalendar(editingCalendar.value.id)

    if (
      (state.currentStep === 6 && showCalendarResourcesTab.value) ||
      (state.currentStep === 5 && !showCalendarResourcesTab.value)
    ) {
      AppState.editingCalendar = null
      toggleAdvancedView()
      AppState.selectedCalendarId = ''
      AppState.isNewCalendar = false
      await getCalendarsInLocation(true)
    }

    state.isSaving = false

    if (
      state.currentStep === 3 &&
      !AppState.editCalendarValidationData.haveMerchant &&
      editingCalendar.value?.calendarType !== CalendarType.EVENT
    ) {
      state.currentStep += 2
    } else if (
      !state.showConnectionSyncModal &&
      !AppState.isUserAvailabilityUpdated
    ) {
      state.currentStep += 1
    }
    methods.updateShowCalendarSyncModal(false)
    AppState.isConnectionSyncLoading = false

    //reset the isUserAvailabilityUpdated flag
    AppState.isUserAvailabilityUpdated = false
  },
  async updateRoomsAndEquipments(roomsAndEquipments) {
    const calendarId = editingCalendar.value.id
    const {
      selectedEquipments,
      selectedRooms,
      toRemoveEquipments,
      toRemoveRooms,
    } = roomsAndEquipments
    const allRooms: any[] = AppState.globalRooms
    const allEquipments: any[] = AppState.globalEquipments
    const roomsToUpdate: any[] = [],
      equipmentsToUpdate: any[] = []
    for (const room of allRooms) {
      if (
        !room?.calendarIds?.includes(calendarId) &&
        selectedRooms?.includes(room._id)
      ) {
        roomsToUpdate.push(room._id)
      }
    }
    const promises: any[] = []
    for (const toUpdateRoom of roomsToUpdate) {
      promises.push(
        CalendarResourceService.update(
          toUpdateRoom,
          { calendarIds: [calendarId] },
          CALENDAR_RESOURCE_TYPES.ROOMS
        )
      )
    }
    for (const removeRoom of toRemoveRooms) {
      if (
        allRooms
          ?.find((e: any) => e._id === removeRoom)
          ?.calendarIds?.includes(calendarId)
      ) {
        promises.push(
          CalendarResourceService.update(
            removeRoom,
            { toDeleteCalendarIds: [calendarId] },
            CALENDAR_RESOURCE_TYPES.ROOMS
          )
        )
      }
    }
    for (const equipment of allEquipments) {
      if (
        !equipment?.calendarIds?.includes(calendarId) &&
        selectedEquipments?.includes(equipment._id)
      ) {
        equipmentsToUpdate.push(equipment._id)
      }
    }
    for (const toUpdateEquipment of equipmentsToUpdate) {
      promises.push(
        CalendarResourceService.update(
          toUpdateEquipment,
          { calendarIds: [calendarId] },
          CALENDAR_RESOURCE_TYPES.EQUIPMENTS
        )
      )
    }
    for (const removeEquipment of toRemoveEquipments) {
      promises.push(
        CalendarResourceService.update(
          removeEquipment,
          { toDeleteCalendarIds: [calendarId] },
          CALENDAR_RESOURCE_TYPES.EQUIPMENTS
        )
      )
    }
    await Promise.all(promises)
    await Promise.all([getRoomsInLocation(true), getEquipmentsInLocation(true)])
  },
  updateShowCalendarSyncModal(showModal: boolean) {
    state.showConnectionSyncModal = showModal
  },
}

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const steps = computed(() => {
  return [
    {
      title: isServiceCalendar.value
        ? t('calendar_advanced.general.service_details')
        : t('calendar_advanced.general.meeting_details'),
      id: 'meeting-details',
    },
    {
      title: t('calendar_advanced.general.availability'),
      id: 'availability',
    },
    {
      title: t('calendar_advanced.general.forms_and_payment'),
      id: 'forms',
    },
    {
      title: t(
        'calendar_advanced.general.notifications_and_additional_options'
      ),
      id: 'notifications',
    },
    {
      title: t('calendar_advanced.general.connections'),
      id: 'connections',
    },
    {
      title: t('calendar_advanced.general.customizations'),
      id: 'customizations',
    },
  ]
})

const currentStepId = computed(() => steps.value[state.currentStep]?.id)
const eventAlertId = computed(() =>
  currentStepId.value
    ? `event-calendar-${currentStepId.value}-alert`
    : 'event-calendar-alert'
)

const selectedCalendarData = computed(() => {
  const data: ICalendarShare = {
    id: editingCalendar.value?.id,
    slug: editingCalendar.value?.widgetSlug,
    name: editingCalendar.value?.name,
    calendarType: editingCalendar.value?.calendarType,
    providerId: editingCalendar.value?.providerId,
    slotDuration: editingCalendar.value?.slotDuration.toString(),
    slotDurationUnit: editingCalendar.value?.slotDurationUnit,
    widgetType: editingCalendar.value?.widgetType,
    source: SHARE_SOURCE.CALENDAR,
    schedulePath: 'bookings',
    permanentPath: 'booking',
    otlPath: 'otl',
    showOneTimeLink: true,
    isSaving: showMessage.value,
    loadingMessage: showMessage.value
      ? t('calendar_advanced.general.saving_changes')
      : '',
  }
  return data
})

watch(
  () => editingCalendar.value,
  async newValue => {
    if (
      (isRoomEnabled() || isEquipmentEnabled()) &&
      newValue?.isServiceCalendar
    ) {
      steps.value.push({
        title: t('calendar_advanced.general.equipments'),
        id: 'equipments',
      })
      await Promise.all([getRoomsInLocation(), getEquipmentsInLocation()])
      showCalendarResourcesTab.value = true
    }
  }
)

watch(
  () => [state.currentStep, editingCalendar.value?.calendarType],
  () => {
    state.currentQuickTip = getQuickTip(
      steps.value[state.currentStep]?.id,
      editingCalendar.value?.calendarType,
      t
    )
  },
  { immediate: true }
)

const notification = useNotification()
function copyCalendarId(id) {
  copyToClipboard(id)
  notification.info({
    content: t('settings.calendar.copied_to_clipboard'),
    duration: 1 * 1000,
  })
}

const handleShareCalendar = async () => {
  showShareCalendarModal.value = true
  await methods.handleSave()
}

const handleTroubleshoot = async () => {
  await methods.handleSave()
  AppState.parentApp?.then(parent => {
    parent.emit('open-troubleshoot-calendar', {
      calendarId: editingCalendar.value?.id,
    })
  })
}

const showMessage = computed(() => {
  console.log('showMessage', state.isSaving)
  return state.isSaving
})

methods.loadData()
const debouncedCalendarRefresh = _.debounce(methods.refreshCalendar, 2000)
</script>

<template>
  <div class="flex w-full flex-col justify-center">
    <div
      class="mb-7 flex w-full items-center justify-between rounded-sm px-5 py-3 text-sm shadow-md"
    >
      <UIButton
        id="create-calendar-close"
        type="default"
        :quaternary="true"
        @click="methods.handleCancel"
      >
        <div class="flex flex-row items-center gap-1">
          <ArrowNarrowLeftIcon class="h-5 w-5 text-gray-700" />
          <div class="font-semibold text-gray-700">
            {{ $t('settings.calendar.back_message') }}
          </div>
        </div>
      </UIButton>
      <div class="flex flex-col items-start gap-[5px] pl-3 text-xl">
        {{
          AppState.isNewCalendar
            ? $t('calendar_advanced.general.create')
            : $t('calendar_advanced.general.edit')
        }}
        -
        {{ editingCalendar?.name || '' }}
        <div v-if="editingCalendar?.id" class="text-sm font-normal text-gray-400">
          <UIButton
            id=""
            class="w-3/4 cursor-pointer text-gray-700"
            :text="true"
            @click="copyCalendarId(editingCalendar.id)"
          >
            <div class="flex flex-row items-center gap-1">
              {{ t('calendar_advanced.general.calendar_id') }}:
              {{ editingCalendar?.id }}
              <Copy04Icon class="h-3 w-3" />
            </div>
          </UIButton>
        </div>
      </div>

      <div class="flex space-x-3">
        <UITooltip trigger="hover">
          <template #trigger>
            <UIButton
              :id="`share-calendar-${editingCalendar?.id}`"
              type="default"
              @click="handleShareCalendar"
            >
              <Share06Icon class="h-5 w-5 text-gray-700" />
            </UIButton>
          </template>
          <div>
            {{ $t('settings.calendar.share_calendar') }}
          </div>
        </UITooltip>
        <UITooltip trigger="hover">
          <template #trigger>
            <UIButton
              :id="`troubleshoot-calendar-${editingCalendar?.id}`"
              type="default"
              @click="handleTroubleshoot"
            >
              <Tool02Icon class="h-5 w-5 text-gray-700" />
            </UIButton>
          </template>
          <div>
            {{ $t('settings.calendar.troubleshoot_calendar_link') }}
          </div>
        </UITooltip>
        <UIButton
          id="create-calendar-save"
          type="primary"
          :loading="state.isSaving"
          @click="methods.handleSave"
        >
          {{ $t('calendar_advanced.general.save') }}
        </UIButton>
      </div>
    </div>

    <div class="flex w-full justify-center space-x-14 p-3">
      <div class="flex w-40 flex-col space-y-3">
        <NavLink
          v-for="(step, ind) in steps"
          :key="step.title"
          :class="{
            hidden:
              step.id === 'connections' &&
              !AppState.editCalendarValidationData?.haveMerchant &&
              editingCalendar?.calendarType !== CalendarType.EVENT,
          }"
          :is-active="state.currentStep === ind"
          :text="step.title"
          @click="() => (state.currentStep = ind)"
        />

        <div class="flex flex-col space-y-2 pt-10">
          <div class="flex items-center space-x-2">
            <Lightbulb02Icon class="h-5 w-5 text-gray-400" />
            <div class="text-md font-medium text-gray-400">
              {{ $t('calendar_advanced.general.quick_tip') }}
            </div>
          </div>
          <div class="pl-1 text-xs text-gray-400" v-html="state.currentQuickTip"/>
        </div>
      </div>

      <div
        class="flex max-w-4xl flex-grow flex-col max-h-[calc(100vh-10rem)]"
      >
        <div
          v-if="editingCalendar?.calendarType === CalendarType.EVENT"
          class="mb-4 flex-shrink-0"
        >
          <EventCalendarAlert :id="eventAlertId" />
        </div>

        <div
          class="max-w-4xl flex-grow overflow-y-auto rounded-xl border bg-white p-10 shadow-md"
          :class="{
            'pointer-events-none blur-md filter':
              AppState.isAdvancedCalendarPageLoading,
          }"
        >
          <div
            :class="{
              hidden: state.currentStep !== 0,
            }"
          >
            <MeetingDetailsPage
              ref="meetingDetailsPage"
              @on-save="data => methods.updateCalendar(data, 0)"
            />
          </div
          >

          <div
            :class="{
              hidden: state.currentStep !== 1,
            }"
          >
            <NewAvailabilityPageWithSchedule
              v-if="AppState.isScheduleEnabled"
              ref="availabilityPage"
              @on-save="data => methods.updateCalendar(data, 1)"
              @save-calendar="methods.handleSave()"
            />
            <NewAvailabilityPage
              v-else
              ref="availabilityPage"
              @on-save="data => methods.updateCalendar(data, 1)"
            />
          </div
          >

          <div
            :class="{
              hidden: state.currentStep !== 2,
            }"
          >
            <FormsPage
              ref="formPage"
              @on-save="data => methods.updateCalendar(data, 2)"
            />
          </div
          >

          <div
            :class="{
              hidden: state.currentStep !== 3,
            }"
          >
            <NotificationsPageV2
              ref="notificationsPage"
              @on-save="data => methods.updateCalendar(data, 3)"
            />
          </div
          >

          <div
            :class="{
              hidden: state.currentStep !== 4,
            }"
          >
            <ConnectionsPage
              ref="connectionsPage"
              :showConnectionSyncModal="state.showConnectionSyncModal"
              :update-show-connection-sync-modal="
                methods.updateShowCalendarSyncModal
              "
              @on-save="methods.handleSave"
              @on-cancel="methods.handleCancel"
            />
          </div
          >

          <div
            :class="{
              hidden: state.currentStep !== 5,
            }"
          >
            <ExtrasPage
              ref="extrasPage"
              @on-save="data => methods.updateCalendar(data, 5)"
            />
          </div
          >

          <div
            v-if="showCalendarResourcesTab"
            :class="{ hidden: state.currentStep !== 6 }"
          >
            <RoomsAndEquipmentsPage
              ref="roomsPage"
              @on-save="data => methods.updateCalendar(data, 6)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <CalendarShareModal
    v-if="showShareCalendarModal"
    :show="showShareCalendarModal"
    :data="selectedCalendarData"
    @close-modal="showShareCalendarModal = false"
  />
</template>
